import { apiRequest } from "../utils/api";
import { getToken } from "../utils/auth";

const shouldUseStream = () => true;

const evaluateAnswer = async ({ sessionId, subjectId, questionId, questionText, answer }) => {
  const response = await apiRequest.post("/api/ai/evaluate-answer", {
    sessionId,
    subjectId,
    questionId,
    questionText,
    answer,
  });
  return response.data;
};

const getNudgeStatus = async (jobId) => {
  const response = await apiRequest.get(`/api/ai/nudge/${jobId}`);
  return response.data;
};

const getSessionNudgeLogs = async (sessionId) => {
  const response = await apiRequest.get(`/api/ai-nudge-logs/session/${sessionId}`);
  return response.data;
};

const getUserNudgeLogs = async (userId) => {
  const response = await apiRequest.get(`/api/ai-nudge-logs/user/${userId}`);
  return response.data;
};

const getNudgeLogAnalytics = async (params) => {
  const response = await apiRequest.get("/api/ai-nudge-logs/analytics", { params });
  return response.data;
};

const getNudgeLogSessions = async () => {
  const response = await apiRequest.get("/api/ai-nudge-logs/sessions");
  const data = response.data;
  return Array.isArray(data) ? data : [];
};

const getStreamAuthHeader = () => {
  const token = getToken();
  return token ? `Bearer ${token}` : "";
};

const evaluateAnswerStream = async (
  { sessionId, questionId, questionText, answer, subjects, sessionAnswers },
  onChunk,
  onComplete,
  onError
) => {
  // Validate required fields
  if (!sessionId) {
    const error = new Error("sessionId is required.");
    if (onError) onError(error);
    return;
  }

  const baseURL = process.env.REACT_APP_COACHING_WEB_API;
  const authHeader = getStreamAuthHeader();

  const response = await fetch(`${baseURL}api/ai/evaluate-answer-stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      Authorization: authHeader,
    },
    body: JSON.stringify({ sessionId, questionId, questionText, answer, subjects, sessionAnswers }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error || `Stream request failed with status: ${response.status}`);
  }

  if (!response.body) {
    throw new Error("Response body is null");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let fullNudge = "";

  const readStream = async () => {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split("\n");

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          try {
            const data = JSON.parse(line.slice(6))

            if (data.token) {
              fullNudge += data.token;
              onChunk?.(data.token);
            }

            if (data.nudge) {
              onComplete?.({
                nudge: data.nudge,
                evaluationType: data.evaluationType,
                usage: data.usage,
              });
            }

            if (data.message) {
              onError?.(data.message);
            }
          } catch (parseErr) {
            console.warn("Failed to parse SSE data:", parseErr);
          }
        }
      }
    }
    return fullNudge;
  };

  return readStream();
};

export default {
  evaluateAnswer,
  getNudgeStatus,
  getSessionNudgeLogs,
  getUserNudgeLogs,
  getNudgeLogAnalytics,
  getNudgeLogSessions,
  evaluateAnswerStream,
  shouldUseStream,
};
